import { useMemo, useState } from 'react';
import { fetchPartnerDirectory, PartnerMapData, PartnerPin } from '../../api/partnerMap';
import { useStateMap } from '../../hooks/useStateMap';
import PartnerResultsMap, {
  PartnerMapConfig, PartnerSectionHead, usePartnerFilter,
} from './PartnerResultsMap';

type PartnerDirectoryProps = {
  eyebrow?: string;
  heading: string;
  lede?: string;
  /** Icon key for every partner pin — one shared style. */
  markerIcon?: string;
  /** Name of a live choice-type FormField on the tenant's `partner` form to
   * offer as a visitor filter (see partnersApi.filterableFields). */
  filterField?: string;
};

const matches = (pin: PartnerPin, query: string) =>
  [pin.school_name, pin.city, pin.state, pin.street]
    .some((part) => (part ?? '').toLowerCase().includes(query));

/**
 * The `PartnerDirectory` registered section type (see
 * tenant_site/section_types.py, tenant_site/system_pages.py's PartnersPage) —
 * the searchable list + map of every published partner, tenant-wide, laid out
 * by the shared `PartnerResultsMap` with a search box over name/city/state.
 *
 * One request supplies everything: `GET /api/partners/directory/` returns the
 * pins *and* the base map to draw them on — the overview's states plus a
 * polygon for every state that holds a pin — so no pin lands on blank paper. It
 * answers only while a directory section is showing on an active page, and this
 * component is its sole caller; a 404 therefore means "no directory configured".
 */
export default function PartnerDirectory({
  eyebrow, heading, lede, markerIcon, filterField,
}: PartnerDirectoryProps) {
  const request = useStateMap<PartnerMapData>(
    (signal) => fetchPartnerDirectory(filterField, signal),
    [filterField],
  );
  const data = request.status === 'ready' ? request.data : undefined;
  const [query, setQuery] = useState('');

  const { filterValues, activeFilter, setActiveFilter, filtered } = usePartnerFilter(
    data?.pins ?? [], filterField,
  );
  const pins = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return filtered
      .filter((pin) => !needle || matches(pin, needle))
      .sort((a, b) => a.school_name.localeCompare(b.school_name));
  }, [filtered, query]);

  const hasMap = !!data?.map && data.map.features.length > 0;

  return (
    <section className="states partner-map partner-directory section" data-testid="partner-directory">
      <div className="wrap">
        <PartnerSectionHead eyebrow={eyebrow} heading={heading} lede={lede} />
        <PartnerMapConfig testIdPrefix="partner-directory" markerIcon={markerIcon} filterField={filterField} />
        <PartnerResultsMap
          testIdPrefix="partner-directory"
          pins={pins}
          loaded={!!data}
          emptyMessage={
            request.status === 'loading'
              ? 'Loading schools…'
              : request.status === 'ready'
                ? (query || activeFilter ? 'No schools match your search.' : 'No partner schools yet.')
                : 'The partner directory is unavailable right now.'
          }
          mapData={hasMap ? data!.map : undefined}
          mapPlaceholder={
            request.status === 'loading'
              ? 'Loading map…'
              : request.status === 'ready'
                ? 'No partner schools to show on the map yet.'
                : 'The map is unavailable right now.'
          }
          mapAriaLabel="Map of partner schools"
          markerIcon={markerIcon}
          renderAddress={(pin) => [pin.city, pin.state].filter(Boolean).join(', ')}
          controlsBefore={
            <input
              type="search"
              className="partner-directory-search"
              placeholder="Search by school, city or state"
              aria-label="Search partner schools"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              data-testid="partner-directory-search-input"
            />
          }
          controlsAfter={
            filterField && filterValues.length > 0 ? (
              <select
                aria-label={data?.filter_field_label ?? 'Filter'}
                data-testid="partner-directory-filter-select"
                value={activeFilter}
                onChange={(event) => setActiveFilter(event.target.value)}
              >
                <option value="">{`${data?.filter_field_label ?? 'Filter'}: All`}</option>
                {filterValues.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            ) : null
          }
        />
      </div>
    </section>
  );
}
